<template>
  <v-dialog
    v-model="dialogVisible"
    :max-width="isFullscreen ? 'auto' : '1200px'"
    :fullscreen="isFullscreen"
    :transition="isFullscreen ? 'dialog-bottom-transition' : 'dialog-transition'"
    class="data-preview-dialog">
    <v-card class="d-flex flex-column" :style="{ height: isFullscreen ? '100vh' : '80vh' }">
      <!-- Header -->
      <v-card-title class="d-flex align-center justify-space-between pa-4 bg1">
        <div class="d-flex align-center">
          <v-icon class="mr-3" color="info">mdi-table</v-icon>
          <div>
            <h3>Data Preview</h3>
            <p class="text-caption mb-0 text--secondary">Showing {{ internalPreviewData.length }} rows</p>
          </div>
        </div>
        <div class="d-flex">
          <v-btn
            icon
            @click="toggleFullscreen"
            :title="isFullscreen ? 'Exit Full Screen (F11)' : 'Enter Full Screen (F11)'">
            <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          </v-btn>
          <v-btn icon @click="closeDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </v-card-title>

      <!-- Search -->
      <v-card-subtitle class="pa-4 pb-2">
        <div class="d-flex align-items-center mt-3">
          <v-text-field
            v-model="searchText"
            append-icon="mdi-magnify"
            label="Search preview data"
            dense
            outlined
            hide-details
            clearable
            class="mr-4"
            style="max-width: 300px">
          </v-text-field>
        </div>
      </v-card-subtitle>

      <!-- Preview Content -->
      <v-card-text class="flex-grow-1 pa-0">
        <!-- Table View -->
        <v-data-table
          :headers="previewHeaders"
          :items="tableItems"
          :items-per-page="50"
          :items-per-page-options="[25, 50, 100, 250, 500, -1]"
          class="data-preview-table elevation-0"
          dense
          fixed-header
          :loading="loading"
          :height="isFullscreen ? 'calc(100vh - 200px)' : 'calc(80vh - 280px)'"
          :footer-props="{
            'items-per-page-text': 'Rows per page:',
            'items-per-page-all-text': 'All',
            'items-per-page-options': [25, 50, 100, 250, 500, -1],
          }"
          :search="searchText"
          multi-sort>
          <!-- Custom header for mapped columns -->
          <template v-for="header in previewHeaders" v-slot:[`header.${header.value}`]>
            <div :key="header.value" class="d-flex flex-column">
              <span
                :class="{
                  'font-weight-bold': true,
                  'text-primary': header.mappedTo,
                  'text--secondary': !header.mappedTo,
                }">
                {{ header.text }}
              </span>
              <v-chip
                v-if="header.mappedTo"
                x-small
                color="primary"
                style="width: fit-content"
                class="mt-1">
                <v-icon size="16" class="mr-2">mdi-check</v-icon> {{ header.mappedTo }}
              </v-chip>
            </div>
          </template>

          <!-- Custom cell rendering with highlighting -->
          <template v-for="header in previewHeaders" v-slot:[`item.${header.value}`]="{ item }">
            <div
              v-if="hasPreviewValue(item[header.value]) || isEditing"
              :key="header.value"
              :class="{
                'preview-cell': true,
                'mapped-cell': header.mappedTo,
                'unmapped-cell': !header.mappedTo,
                'identity-cell': header.isIdentity,
                'pa-0': isEditing,
                'px-4': !isEditing,
              }">
              <template v-if="isEditing">
                <v-text-field
                  v-model="editedData[item._rowIndex][header.value]"
                  dense
                  hide-details
                  solo
                  flat
                  class="ma-0 pa-0"
                  :class="{
                    'edited-cell': isEditing && isCellChanged(item._rowIndex, header.value),
                  }"
                  @input="markCellChanged(item._rowIndex, header.value)"
                />
              </template>
              <template v-else>
                <span
                  :class="{
                    'text-primary font-weight-medium': header.mappedTo,
                    'text--secondary': !header.mappedTo,
                    'identity-text': header.isIdentity,
                  }">
                  {{ item[header.value] }}
                </span>
              </template>
            </div>
          </template>

          <template v-slot:loading>
            <div class="text-center pa-4">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <p class="mt-2">Loading preview...</p>
            </div>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-4">
              <v-icon large color="grey">mdi-table-large</v-icon>
              <p class="text--secondary mt-2">No preview data available</p>
            </div>
          </template>
        </v-data-table>
      </v-card-text>

      <!-- Actions -->
      <v-card-actions class="pa-4 bg1">
        <v-btn @click="closeDialog"> Close </v-btn>
        <v-spacer></v-spacer>
        <v-btn v-if="!isEditing" @click="startEditing">
          <v-icon left>mdi-pencil</v-icon>
          Edit
        </v-btn>
        <template v-else>
          <v-btn text color="secondary" @click="cancelEditing">
            <v-icon left>mdi-cancel</v-icon>
            Cancel
          </v-btn>
          <v-btn color="primary" @click="saveEdits" :disabled="saving">
            <v-icon left>mdi-content-save</v-icon>
            Save Changes
          </v-btn>
        </template>
        <v-btn
          color="primary"
          @click="proceedWithMapping"
          :disabled="Object.keys(columnMappings).length === 0 || isEditing">
          <v-icon left>mdi-check</v-icon> Continue</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'DataPreviewDialog',
    props: {
      // Register previews retain every submitted row; existing tag-import consumers keep filtering.
      preserveRows: { type: Boolean, default: false },
      value: {
        type: Boolean,
        default: false,
      },
      previewData: {
        type: Array,
        default: () => [],
      },
      columnMappings: {
        type: Object,
        default: () => ({}),
      },
      identityColumn: {
        type: String,
        default: '',
      },
      availableFields: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      totalRows: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        dialogVisible: false,
        isFullscreen: false,
        searchText: '',
        
        // inline editing state
        isEditing: false,
        editedData: [],
        changedCells: {},
        saving: false,
        // internal working copy of preview data
        internalPreviewData: [],
      };
    },
    computed: {
      tableItems() {
        // add a stable row index for editing lookup
        return (this.internalPreviewData || []).map((row, index) => ({ _rowIndex: index, ...row }));
      },
      previewHeaders() {
        if (this.internalPreviewData.length === 0) return [];

        // Get all column names from the first row, excluding internal fields
        const columnNames = Object.keys(this.internalPreviewData[0] || {}).filter(
          (key) => !key.startsWith('_'),
        );

        return columnNames.map((columnName) => {
          const mappedField = this.columnMappings[columnName];
          const avaField = mappedField
            ? this.availableFields.find((f) => f.key === mappedField)
            : null;

          return {
            text: columnName,
            value: columnName,
            sortable: false,
            mappedTo: avaField ? avaField.label : null,
            isMapped: !!mappedField,
            isIdentity: !!this.identityColumn && mappedField === this.identityColumn,
            width: 'auto',
            minWidth: '150',
          };
        });
      },

      unmappedColumns() {
        return this.previewHeaders.filter((h) => !h.isMapped);
      },

      hasTagNoMapping() {
        return Object.values(this.columnMappings).includes('name');
      },
    },
    watch: {
      value: {
        immediate: true,
        handler(newVal) {
          this.dialogVisible = newVal;
        },
      },
      dialogVisible(newVal) {
        this.$emit('input', newVal);
        if (newVal) {
          // Add keyboard listener when dialog opens
          document.addEventListener('keydown', this.handleKeydown);
          // Only sync from prop if internal data is empty (first time opening)
          if (this.internalPreviewData.length === 0) {
            this.syncInternalFromProp();
          }
        } else {
          // Remove keyboard listener when dialog closes
          document.removeEventListener('keydown', this.handleKeydown);
          
        }
      },
      previewData: {
        deep: true,
        handler(newData, oldData) {
          // Only sync from prop if this is a genuine external data change
          // (not just the component re-emitting the same data back)
          if (newData !== oldData && JSON.stringify(newData) !== JSON.stringify(this.internalPreviewData)) {
            this.syncInternalFromProp();
            if (this.dialogVisible && this.isEditing) this.resetEditingState();
          }
        }
      }
    },
    methods: {
      hasPreviewValue(cellValue) {
        return cellValue !== null && cellValue !== undefined && cellValue !== '';
      },
      syncInternalFromProp() {
        const raw = JSON.parse(JSON.stringify(this.previewData || []));
        if (this.preserveRows) {
          this.internalPreviewData = raw;
          return;
        }
        // Apply filtering: keep only rows that have a non-empty identity value
        try {
          const identityExcelColumn = Object.keys(this.columnMappings || {}).find(
            (excelCol) => this.columnMappings[excelCol] === this.identityColumn,
          );

          if (!identityExcelColumn) {
            this.internalPreviewData = raw;
            return;
          }

          const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';

          this.internalPreviewData = raw.filter((row) => {
            if (!row) return false;
            const identityHas = hasValue(row[identityExcelColumn]);
            return identityHas;
          });
        } catch (e) {
          this.internalPreviewData = raw;
        }
      },

      // Method to force refresh from prop (can be called by parent if needed)
      forceRefreshFromProp() {
        this.syncInternalFromProp();
        if (this.isEditing) {
          this.resetEditingState();
        }
      },

      startEditing() {
        this.isEditing = true;
        // deep clone previewData to edit safely without mutating prop
        this.editedData = JSON.parse(JSON.stringify(this.internalPreviewData || []));
        this.changedCells = {};
      },

      cancelEditing() {
        this.resetEditingState();
      },

      resetEditingState() {
        this.isEditing = false;
        this.editedData = [];
        this.changedCells = {};
        this.saving = false;
      },

      isCellChanged(rowIndex, fieldName) {
        const key = `${rowIndex}::${fieldName}`;
        return !!this.changedCells[key];
      },

      markCellChanged(rowIndex, fieldName) {
        if (!this.isEditing) return;
        const original = (this.internalPreviewData[rowIndex] || {})[fieldName];
        const current = (this.editedData[rowIndex] || {})[fieldName];
        const key = `${rowIndex}::${fieldName}`;
        // mark changed if value differs, else clear flag
        if (current !== original) {
          this.$set(this.changedCells, key, true);
        } else if (this.changedCells[key]) {
          this.$delete(this.changedCells, key);
        }
      },

      saveEdits() {
        if (!this.isEditing) return;
        this.saving = true;
        try {
          // Build updated dataset by applying edited values to original rows
          const headers = this.previewHeaders.map(h => h.value);
          const updated = (this.internalPreviewData || []).map((row, index) => {
            const editedRow = this.editedData[index] || {};
            const nextRow = { ...row };
            for (const field of headers) {
              nextRow[field] = editedRow[field];
            }
            return nextRow;
          });
          // Apply locally so UI reflects immediately
          this.internalPreviewData = updated;
          // Emit to parent to persist; parent can update previewData or use .sync
          this.$emit('save-edits', updated);
          this.$emit('update:previewData', updated);
          // Locally reset editing mode; assume parent will re-provide updated data
          this.resetEditingState();
        } finally {
          this.saving = false;
        }
      },

      getFieldLabel(fieldKey) {
        const field = this.availableFields.find((f) => f.key === fieldKey);
        return field ? field.label : fieldKey;
      },

      closeDialog() {
        this.isFullscreen = false;
        if (this.isEditing) {
          this.resetEditingState();
        }
        this.dialogVisible = false;
      },

      toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;
      },


      refreshPreview() {
        this.$emit('refresh-preview');
      },

      proceedWithMapping() {
        this.$emit('proceed-with-mapping');
        this.closeDialog();
      },

      loadMoreData() {
        this.$emit('load-more-data');
      },

      handleKeydown(event) {
        // F11 or Ctrl+Shift+F to toggle fullscreen
        if (event.key === 'F11' || (event.ctrlKey && event.shiftKey && event.key === 'F')) {
          event.preventDefault();
          this.toggleFullscreen();
        }
        // Escape to exit fullscreen (but not close dialog)
        else if (event.key === 'Escape' && this.isFullscreen) {
          event.preventDefault();
          this.isFullscreen = false;
        }
      },
    },

    beforeDestroy() {
      // Clean up keyboard listener
      document.removeEventListener('keydown', this.handleKeydown);
    },
  };
</script>

<style scoped>
  .data-preview-dialog .v-dialog {
    overflow-y: auto;
  }

  .preview-cell {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 4px 0;
  }

  .mapped-cell {
    background-color: rgba(33, 150, 243, 0.05);
    /* border-left: 3px solid #2196f3; */
    padding-left: 8px;
  }

  .unmapped-cell {
    background-color: rgba(158, 158, 158, 0.05);
    /* border-left: 3px solid #9e9e9e; */
    padding-left: 8px;
  }

  .identity-cell {
    background-color: rgba(76, 175, 80, 0.08);
    border-left: 3px solid #4caf50;
  }

  .identity-text {
    color: #2e7d32 !important;
    font-weight: 600;
  }

  :deep(.edited-cell > .v-input__control > .v-input__slot) {
    background-color: rgba(255, 193, 7, 0.15);
  }

  /* Enhanced table styling */
  :deep(.v-data-table-header th) {
    border-bottom: 2px solid #e0e0e0 !important;
    vertical-align: top !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }

  :deep(.v-data-table tbody tr:nth-child(even)) {
    background-color: rgba(0, 0, 0, 0.02);
  }

  :deep(.v-data-table tbody tr:hover) {
    background-color: rgba(33, 150, 243, 0.08) !important;
  }

  /* Chip styling */
  .v-chip.v-size--x-small {
    height: 20px;
    font-size: 10px;
  }

  /* Fullscreen specific styling */
  .data-preview-dialog :deep(.v-dialog--fullscreen) {
    overflow: hidden;
  }

  .data-preview-dialog :deep(.v-dialog--fullscreen .v-card) {
    overflow: hidden;
  }

  /* Enhanced fullscreen table experience */
  :deep(.v-dialog--fullscreen .v-data-table__wrapper) {
    height: calc(100vh - 300px) !important;
    overflow-y: auto;
  }

  /* Improved header visibility in fullscreen */
  :deep(.v-dialog--fullscreen .v-data-table-header) {
    position: sticky !important;
    top: 0 !important;
    z-index: 2 !important;
    background: white !important;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;
  }

   :deep(.data-preview-table) {
    flex: 1;
    margin: 12px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    overflow: hidden !important;
  }

  :deep(.data-preview-table .v-data-table__wrapper > table tr td) {
    padding: 0 !important;
    border: 1px solid #e6e6e688 !important;
  }

  :deep(.data-preview-table .v-data-table-header th) {
    border: 1px solid #e6e6e638 !important;
  }

  
</style>
